/**
 * AccessRequestGate
 *
 * After consent is granted, checks if user has org access.
 * - First user (client role): passes through immediately
 * - Approved user: passes through
 * - Pending request: shows "Awaiting Approval" screen
 * - Rejected request: shows "Request Rejected" + retry button
 * - No request: shows "Request Access" screen, creates request on click
 *
 * Wraps embedded dashboard children, placed after EmbeddedConsentGate.
 */

import { useCallback, useState, useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Clock, CheckCircle, XCircle, UserPlus, Loader2, RefreshCw } from 'lucide-react';
import { useEmbeddedOrg } from '@/contexts/EmbeddedOrgContext';
import { useEmbeddedAuthFetch } from '@/hooks/useEmbeddedAuthFetch';
import { embeddedGateHeightClass } from '@/lib/embedded-host';

interface AccessRequestGateProps {
  children: ReactNode;
}

type RequestStatus =
  'loading' | 'approved' | 'approvedNoRole' | 'pending' | 'rejected' | 'none' | 'error';

export function AccessRequestGate({ children }: AccessRequestGateProps) {
  const { t } = useTranslation(['onboarding', 'common']);
  const {
    orgId,
    role,
    isLoading: orgLoading,
    refreshSession,
    crmVendor,
  } = useEmbeddedOrg();
  const screenHeight = embeddedGateHeightClass(crmVendor, 'h-screen');
  const { authenticatedFetch } = useEmbeddedAuthFetch();

  const [status, setStatus] = useState<RequestStatus>('loading');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One session re-read per approval found: a request can be approved while
  // the membership it should have created is missing (the approval commits
  // first), and re-reading without end would loop status → refresh → status.
  const refreshedForApproval = useRef(false);

  const checkStatus = useCallback(async () => {
    if (!orgId) return;

    try {
      setStatus('loading');
      const data = await authenticatedFetch<{ status: string }>(
        '/api/v1/embedded/access-request/status'
      );

      switch (data.status) {
        case 'approved':
          if (role) {
            setStatus('approved');
          } else if (refreshedForApproval.current) {
            // Re-read once and still no role: say so, with a retry — never
            // the dashboard on role=null, never another automatic round.
            setStatus('approvedNoRole');
          } else {
            // Approval gave this user a role the session read before it does
            // not carry: re-read it; the effect below lets the app in once the
            // role arrives (or asks again, landing in the branch above).
            refreshedForApproval.current = true;
            await refreshSession();
          }
          break;
        case 'pending':
          setStatus('pending');
          break;
        case 'rejected':
          setStatus('rejected');
          break;
        default:
          setStatus('none');
      }
    } catch {
      setStatus('error');
    }
  }, [authenticatedFetch, orgId, role, refreshSession]);

  // If user already has a role (is in org), they're approved
  useEffect(() => {
    if (orgLoading) return;

    if (role) {
      // User has a role = already in org = approved
      setStatus('approved');
      return;
    }

    // No role = not in org. Check access request status.
    if (!orgId) {
      setStatus('error');
      return;
    }

    void checkStatus();
  }, [checkStatus, orgLoading, role, orgId]);

  const handleRequestAccess = async () => {
    if (!orgId) return;

    try {
      setSubmitting(true);
      setError(null);

      await authenticatedFetch('/api/v1/embedded/access-request', {
        method: 'POST',
        body: JSON.stringify({}),
      });

      setStatus('pending');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('common:errors.unknown', 'Unknown error')
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Loading
  if (status === 'loading' || orgLoading) {
    return (
      <div
        className={`flex ${screenHeight} items-center justify-center bg-bg`}
        role="status"
      >
        <Loader2 className="h-8 w-8 animate-spin text-fg-muted" />
      </div>
    );
  }

  // Approved — render children (dashboard)
  if (status === 'approved') {
    return <>{children}</>;
  }

  // Approved, but the session still carries no role after one re-read
  if (status === 'approvedNoRole') {
    return (
      <div className={`flex ${screenHeight} items-center justify-center bg-bg p-4`}>
        <Card className="w-full max-w-md bg-bg-elevated">
          <CardHeader className="text-center">
            <Clock className="h-12 w-12 text-warning mx-auto mb-2" />
            <CardTitle className="text-lg">
              {t('accessRequest.approvedNoRoleTitle', 'Access Approved')}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-fg-muted">
              {t(
                'accessRequest.approvedNoRoleMessage',
                'Your request was approved, but your account is not ready yet. Try again in a minute; if it persists, contact the administrator.'
              )}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                refreshedForApproval.current = false;
                void checkStatus();
              }}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              {t('accessRequest.checkStatus', 'Check Status')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Pending — awaiting admin approval
  if (status === 'pending') {
    return (
      <div className={`flex ${screenHeight} items-center justify-center bg-bg p-4`}>
        <Card className="w-full max-w-md bg-bg-elevated">
          <CardHeader className="text-center">
            <Clock className="h-12 w-12 text-warning mx-auto mb-2" />
            <CardTitle className="text-lg">
              {t('accessRequest.pendingTitle', 'Awaiting Approval')}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-fg-muted">
              {t(
                'accessRequest.pendingMessage',
                'Your access request has been sent to the administrator. You will get access once it is approved.'
              )}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void checkStatus()}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              {t('accessRequest.checkStatus', 'Check Status')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Rejected
  if (status === 'rejected') {
    return (
      <div className={`flex ${screenHeight} items-center justify-center bg-bg p-4`}>
        <Card className="w-full max-w-md bg-bg-elevated">
          <CardHeader className="text-center">
            <XCircle className="h-12 w-12 text-danger mx-auto mb-2" />
            <CardTitle className="text-lg">
              {t('accessRequest.rejectedTitle', 'Access Request Declined')}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-fg-muted">
              {t(
                'accessRequest.rejectedMessage',
                'Your access request was not approved. You can try submitting a new request.'
              )}
            </p>
            <Button onClick={handleRequestAccess} disabled={submitting} className="gap-2">
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4" />
              )}
              {t('accessRequest.retryRequest', 'Request Access Again')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No request yet — show request access screen
  return (
    <div className={`flex ${screenHeight} items-center justify-center bg-bg p-4`}>
      <Card className="w-full max-w-md bg-bg-elevated">
        <CardHeader className="text-center">
          <UserPlus className="h-12 w-12 text-primary mx-auto mb-2" />
          <CardTitle className="text-lg">
            {t('accessRequest.requestTitle', 'Request Access')}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-sm text-fg-muted">
            {t(
              'accessRequest.requestMessage',
              'You need administrator approval to access this application. Click below to send an access request.'
            )}
          </p>
          {error && <p className="text-sm text-danger">{error}</p>}
          <Button
            onClick={handleRequestAccess}
            disabled={submitting || !orgId}
            className="gap-2"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle className="h-4 w-4" />
            )}
            {t('accessRequest.sendRequest', 'Send Access Request')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
